import { onMounted, onUnmounted, ref } from 'vue';

// Shared scroll-reveal observer. Views mark animated blocks with
// `.reveal` (plus `.reveal-left` / `.reveal-right` / `.reveal-blur` /
// `.stagger-children` where a prototype needs them); this composable
// adds `.active` when they enter the viewport and unobserves them.
//
// The `motion` class on <html> is the no-JS / SSR gate: prototype CSS
// only hides `.reveal` blocks under `html.motion`, so pre-rendered HTML
// and no-JS clients always show full content. The class is added only
// client-side, after mount, when IntersectionObserver exists.
export function useScrollReveal(options?: { threshold?: number; rootMargin?: string }) {
  const observer = ref<IntersectionObserver | null>(null);

  onMounted(() => {
    if (typeof document === 'undefined') return;
    const threshold = options?.threshold ?? 0.2;
    const rootMargin = options?.rootMargin ?? '0px';

    if (!('IntersectionObserver' in window)) return;
    document.documentElement.classList.add('motion');

    observer.value = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
            observer.value?.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin, threshold },
    );

    document
      .querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-blur, .stagger-children')
      .forEach((el) => observer.value?.observe(el));
  });

  onUnmounted(() => {
    observer.value?.disconnect();
  });
}
